import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * "Buy more, save more" tiers for a product.
 * Highlights the tier that matches the quantity selector in the same section's product form,
 * and sets that quantity when a tier is chosen. Tier prices are re-rendered from the section
 * response when the variant changes.
 *
 * @typedef {Object} QuantityDiscountsRefs
 * @property {HTMLElement} [tierList]
 * @property {HTMLButtonElement[]} [tiers]
 *
 * @typedef {import('@theme/component-quantity-selector').QuantitySelectorComponent} QuantitySelectorComponent
 *
 * @extends {Component<QuantityDiscountsRefs>}
 */
class QuantityDiscountsComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });

    this.#updateActiveTier();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateActiveTier();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Sets the product form quantity to the chosen tier's minimum quantity.
   * @param {MouseEvent} event - Click on a tier button.
   */
  selectTier(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const tierQuantity = parseInt(button.dataset.quantity ?? '', 10);
    const selector = this.#getQuantitySelector();
    if (!tierQuantity || !selector) return;

    const { min, step } = selector.getCurrentValues();
    const max = selector.getEffectiveMax();

    // Respect the variant's quantity rules: round up to a valid increment, then cap at the max
    let quantity = Math.max(min, tierQuantity);
    quantity = min + Math.ceil((quantity - min) / step) * step;
    if (max !== null) quantity = Math.min(quantity, max);

    selector.setValue(quantity.toString());
    selector.updateButtonStates();
    // Broadcasts the quantity update event, which also refreshes the active tier
    selector.onQuantityChange();
  }

  /**
   * @returns {HTMLElement | null} The product form for this block's product in the same section.
   */
  #getProductForm() {
    const section = this.closest('.shopify-section');
    const { productId } = this.dataset;
    if (!section || !productId) return null;

    return section.querySelector(`product-form-component[data-product-id="${CSS.escape(productId)}"]`);
  }

  /**
   * @returns {QuantitySelectorComponent | null}
   */
  #getQuantitySelector() {
    const selector = this.#getProductForm()?.querySelector('quantity-selector-component');
    if (!selector || !('getCurrentValues' in selector)) return null;

    return /** @type {QuantitySelectorComponent} */ (selector);
  }

  /**
   * @returns {number} The quantity currently entered in the product form.
   */
  #getCurrentQuantity() {
    const input = this.#getProductForm()?.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return 1;

    return parseInt(input.value, 10) || 1;
  }

  #updateActiveTier() {
    const tiers = this.refs.tiers ?? [];
    const quantity = this.#getCurrentQuantity();

    /** @type {HTMLButtonElement | null} */
    let activeTier = null;
    let activeQuantity = 0;

    for (const tier of tiers) {
      const tierQuantity = parseInt(tier.dataset.quantity ?? '', 10);
      if (tierQuantity && quantity >= tierQuantity && tierQuantity > activeQuantity) {
        activeTier = tier;
        activeQuantity = tierQuantity;
      }
    }

    for (const tier of tiers) {
      tier.setAttribute('aria-pressed', String(tier === activeTier));
    }
  }

  /**
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    const form = this.#getProductForm();
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#updateActiveTier();
  };

  /**
   * Replaces the tier list with the one rendered for the newly selected variant.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = String(newProduct.id);
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        const newTierList = html.querySelector(
          `quantity-discounts-component[data-block-id="${CSS.escape(this.dataset.blockId ?? '')}"] [ref="tierList"]`
        );
        const { tierList } = this.refs;
        if (!newTierList || !tierList) return;

        tierList.replaceWith(document.importNode(newTierList, true));
        this.updatedCallback();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quantity-discounts] Product select promise rejected:', error);
      });
  };
}

if (!customElements.get('quantity-discounts-component')) {
  customElements.define('quantity-discounts-component', QuantityDiscountsComponent);
}
